import React, { useState } from 'react'

import { Button, CardHeader, LinearProgress } from '@mui/material'
import CustomerMasterForm from 'features/agencies/components/AgencyForm'
import { useAgencyDelete } from 'features/agencies/hooks/useAgencyDelete'
import { useAgencyUpdate } from 'features/agencies/hooks/useAgencyUpdate'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useNavigate } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { EPlanName } from '@albacross/types'

import { ConfirmModal } from 'components/_base'
import { DescriptionListTable } from 'components/table'

const config = [
  {
    title: 'Price',
    field: 'price',
  },
  {
    title: 'Currency',
    field: 'currency',
  },
  {
    title: 'Usage',
    render: ({ used, total }) => {
      const normalizedTotal = total === Infinity ? '∞' : total
      const color = used > total ? 'secondary' : 'primary'
      const variant = total === Infinity ? 'indeterminate' : 'determinate'
      return (
        <div>
          <p>{`${used} / ${normalizedTotal}`}</p>
          <LinearProgress color={color} value={Math.min((used / total) * 100, 100)} variant={variant} />
        </div>
      )
    },
  },
  {
    title: 'Starter accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Starter),
  },
  {
    title: 'Pro accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Pro),
  },
  {
    title: 'Premium accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Premium),
  },
  {
    title: 'Business accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Business),
  },
  {
    title: 'Free accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Free),
  },
  {
    title: 'Free US accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.FreeUS),
  },
  {
    title: 'AppSumo accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Appsumo),
  },
  {
    title: 'Enterprise accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Enterprise),
  },
  {
    title: 'Self service accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.SelfService),
  },
  {
    title: 'Trial ended accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.TrialEnded),
  },
  {
    title: 'Agency accounts',
    render: ({ usage }) => getUsageRatio(usage, EPlanName.Agency),
  },
]

const getUsageRatio = (usageData: any, planName: any) => {
  try {
    const total = usageData[planName].total
    const normalizedTotal = total === Infinity ? '∞' : total

    return `${usageData[planName].used} / ${normalizedTotal}`
  } catch (_e) {
    return 'Unknown'
  }
}

type AgencyDetailsProps = {
  data?: Record<string, any>
}

const AgencyDetails = ({ data }: AgencyDetailsProps) => {
  const [modal, setModal] = useState<string | null>('')

  const customerId = useCustomerId()
  const navigate = useNavigate()

  const { mutateAsync: deleteAgency } = useAgencyDelete()
  const { mutateAsync: updateAgency } = useAgencyUpdate()

  const handleCloseModal = () => setModal(null)
  const handleOpenModal = (name: string) => () => setModal(name)

  const handleDelete = async () => {
    await deleteAgency()
    navigate(UrlProvider.getUrl('customer', { id: customerId, tab: '' }))
  }

  const handleEdit = async (values: any) => {
    await updateAgency(values)
    handleCloseModal()
  }

  const actions = (
    <>
      <Button onClick={handleOpenModal('edit')}>Edit</Button>
      <Button onClick={handleOpenModal('delete')}>Remove Agency Plan</Button>
    </>
  )

  // Rendered inside a loaded LoadingView, so data is always present here.
  if (!data) return null

  const {
    currency,
    price,
    usage: { business, premium, pro, starter, free, free_us, appsumo, enterprise, agency, self_service, trial_ended },
  } = data

  const initialValues = {
    currency,
    price,
    [EPlanName.Business]: business.total,
    [EPlanName.Premium]: premium.total,
    [EPlanName.Pro]: pro.total,
    [EPlanName.Starter]: starter.total,
    [EPlanName.Free]: free?.total,
    [EPlanName.FreeUS]: free_us?.total,
    [EPlanName.Appsumo]: appsumo?.total,
    [EPlanName.Enterprise]: enterprise?.total,
    [EPlanName.Agency]: agency?.total === Infinity ? -1 : agency?.total,
    [EPlanName.TrialEnded]: trial_ended?.total === Infinity ? -1 : trial_ended?.total,
    [EPlanName.SelfService]: self_service?.total,
  }

  return (
    <>
      <CardHeader action={actions} title="Agency Details" />
      <DescriptionListTable config={config} data={data} titleCellStyle={{ width: 200 }} />

      <CustomerMasterForm
        initialValues={initialValues}
        onClose={handleCloseModal}
        onSubmit={handleEdit}
        open={modal === 'edit'}
      />

      <ConfirmModal
        onClose={handleCloseModal}
        onConfirm={handleDelete}
        open={modal === 'delete'}
        title="Do you want to remove agency plan for this customer?"
      />
    </>
  )
}

export default AgencyDetails
